// GroupParticipantsModal.js - Modal for att hantera deltagare i gruppbokningar

import React, { useState, useEffect, useRef } from 'react';
import {
  getParticipantsWithPayments, addGroupParticipant, removeGroupParticipant,
  updateParticipantStatus, getPatients, registerParticipantPayment,
  getGroupTemplates, getWaitlist, bookFromWaitlist,
  groupPushJournal, ensureRealAppointment, isVirtualOccurrence, occurrenceDateOf,
  isSeriesAppointment, getGroupSeriesInfo, extendGroupSeries, addDaysIso
} from './calendarApi';
import { getJournalTemplates } from '../PatientList/journalApi';
import './Calendar.css';
import NewPatientForm from '../common/NewPatientForm';
import { useScopeChoice } from './ScopeChoiceDialog';
import BookingConfirmationChoice, {
  useConfirmationOptions, defaultChoice, confirmationPayload
} from './BookingConfirmationChoice';
import './GroupParticipantsModal.css';

const PAYMENT_METHODS = [
  { value: 'card', label: 'Kort', icon: 'fa-credit-card' },
  { value: 'cash', label: 'Kontant', icon: 'fa-money-bill-wave' },
  { value: 'swish', label: 'Swish', icon: 'fa-mobile-alt' },
  { value: 'frikort', label: 'Frikort', icon: 'fa-id-card' },
  { value: 'uteblivet', label: 'Uteblivet', icon: 'fa-user-slash' },
  { value: 'avgiftsfri', label: 'Avgiftsfritt (ej fysiskt besök)', icon: 'fa-phone' }
];

// Betalsätt utan inmatat belopp: frikort/avgiftsfritt är 0 kr, uteblivet får
// klinikens fasta avgift av backend
const UTAN_BELOPP = ['frikort', 'uteblivet', 'avgiftsfri'];

// Visningsnamn för ett betalsätt (okända koder visas som de är)
const paymentMethodLabel = (value) =>
  PAYMENT_METHODS.find(m => m.value === value)?.label || value;

// Varje deltagare har en egen bokning per tillfälle (Spår G). I en serie
// frågar vi om ändringen gäller bara detta tillfälle eller även kommande.
const GroupParticipantsModal = ({ appointment, onClose, onUpdate, onOpenPatient }) => {
  const isSeries = isSeriesAppointment(appointment);
  const [scopeDialog, askScope] = useScopeChoice();
  // Omfång för en ändring: utanför serie alltid 'this', annars användarens val (null = avbryt)
  const chooseScope = (question, detail) =>
    (isSeries ? askScope(question, detail) : Promise.resolve('this'));

  // Förläng serien (Spår G)
  const [extendPanel, setExtendPanel] = useState(null);
  const [extending, setExtending] = useState(false);
  const [participants, setParticipants] = useState([]);
  const [passCancelled, setPassCancelled] = useState(appointment?.status === 'cancelled');
  // Omfång valt före skapande av ny patient (Avbryt skapar ingen patient)
  const newPatientScopeRef = useRef('this');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [paymentStats, setPaymentStats] = useState({ paid: 0, unpaid: 0 });

  // Bokningsbekräftelse för deltagare som läggs till (Spår B3): förval från
  // passets bokningstyp/kliniken, gäller sök, ny patient och väntelistan
  const confirmationOptions = useConfirmationOptions();
  const [confirmation, setConfirmation] = useState(null);
  useEffect(() => {
    setConfirmation(defaultChoice(confirmationOptions, appointment?.appointment_type_id));
  }, [confirmationOptions, appointment?.appointment_type_id]);

  // Sök-state för att lägga till nya deltagare
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [adding, setAdding] = useState(false);

  // Inline "ny patient" (G2) - skapa helt ny patient och lägg till som deltagare
  const [showNewPatientForm, setShowNewPatientForm] = useState(false);

  // Betalningsmodal state
  const [paymentModal, setPaymentModal] = useState(null);
  const [paymentAmount, setPaymentAmount] = useState('200');
  const [paymentMethod, setPaymentMethod] = useState('card');
  const [isFriskvard, setIsFriskvard] = useState(false);
  const [processingPayment, setProcessingPayment] = useState(false);

  // Gruppjournal state (G4) - skriv en anteckning, pusha till valda deltagare
  const [showJournalPanel, setShowJournalPanel] = useState(false);
  const [journalText, setJournalText] = useState('');
  const [journalRecipients, setJournalRecipients] = useState(() => new Set());
  const [pushingJournal, setPushingJournal] = useState(false);
  const [journalSuccess, setJournalSuccess] = useState(null);
  // Journalmallar för gruppanteckningen (så den inte blir "Utan mall")
  const [journalTemplates, setJournalTemplates] = useState([]);
  const [selectedJournalTemplateRef, setSelectedJournalTemplateRef] = useState('');

  // Väntelista state
  const [showWaitlist, setShowWaitlist] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [waitlist, setWaitlist] = useState([]);
  const [loadingWaitlist, setLoadingWaitlist] = useState(false);

  // Spår R: ett virtuellt tillfälle i en återkommande serie visas utifrån
  // serien (alla bekräftade, obetalda) och materialiseras först vid första
  // ändring - allt skrivs sedan mot tillfällets egen bokning.
  const realIdRef = useRef(null);

  const loadParticipants = async () => {
    const data = realIdRef.current
      ? await getParticipantsWithPayments(realIdRef.current)
      : await getParticipantsWithPayments(
          appointment.original_appointment_id || appointment.appointment_id,
          occurrenceDateOf(appointment)
        );
    if (data.appointment && !data.appointment.is_virtual) {
      realIdRef.current = data.appointment.appointment_id;
    }
    setPassCancelled(data.appointment?.status === 'cancelled');
    setParticipants(data.participants || []);
    setPaymentStats({
      paid: data.paid_count || 0,
      unpaid: data.unpaid_count || 0
    });
    return data;
  };

  // Tillfällets riktiga boknings-id (materialiserar vid behov). Snabba klick
  // i följd delar på samma pågående anrop i stället för att starta flera.
  const pendingRealRef = useRef(null);
  const ensureRealId = async () => {
    if (realIdRef.current) return realIdRef.current;
    if (!pendingRealRef.current) {
      pendingRealRef.current = ensureRealAppointment(appointment)
        .then(real => {
          realIdRef.current = real.appointment_id;
          return real.appointment_id;
        })
        .finally(() => { pendingRealRef.current = null; });
    }
    return pendingRealRef.current;
  };

  // Hämta deltagare med betalningsstatus
  useEffect(() => {
    realIdRef.current = isVirtualOccurrence(appointment) ? null : appointment?.appointment_id;
    const fetchParticipants = async () => {
      try {
        setLoading(true);
        await loadParticipants();
      } catch (err) {
        console.error('Error fetching participants:', err);
        setError('Kunde inte hämta deltagare');
      } finally {
        setLoading(false);
      }
    };

    if (appointment?.appointment_id) {
      fetchParticipants();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appointment]);

  // Hämta gruppmallar för väntelista
  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        const data = await getGroupTemplates();
        setTemplates(data || []);
      } catch (err) {
        console.error('Error fetching templates:', err);
      }
    };
    fetchTemplates();
  }, []);

  // Hämta journalmallar för gruppanteckningen (samma källa som NewJournalModal).
  // Utan vald mall snapshotas inget mallnamn → anteckningen visas som "Utan mall".
  useEffect(() => {
    const fetchJournalTemplates = async () => {
      try {
        const data = await getJournalTemplates();
        const tpls = data.templates || [];
        setJournalTemplates(tpls);
        // Förvälj första mallen (som NewJournalModal) så man slipper välja varje gång
        if (tpls.length > 0) {
          setSelectedJournalTemplateRef(tpls[0].template_ref);
        }
      } catch (err) {
        console.error('Error fetching journal templates:', err);
      }
    };
    fetchJournalTemplates();
  }, []);

  // Hämta väntelista när mall väljs
  useEffect(() => {
    const fetchWaitlist = async () => {
      if (!selectedTemplate) {
        setWaitlist([]);
        return;
      }
      try {
        setLoadingWaitlist(true);
        const data = await getWaitlist(selectedTemplate);
        setWaitlist(data || []);
      } catch (err) {
        console.error('Error fetching waitlist:', err);
      } finally {
        setLoadingWaitlist(false);
      }
    };
    fetchWaitlist();
  }, [selectedTemplate]);

  // Sök patienter för att lägga till
  useEffect(() => {
    const searchPatients = async () => {
      if (searchQuery.length < 2) {
        setSearchResults([]);
        return;
      }
      try {
        const response = await getPatients(searchQuery);
        const patients = response.patients || response;
        // Filtrera bort aktiva deltagare (avbokade kan läggas till igen)
        const existingIds = participants.filter(p => p.status !== 'cancelled').map(p => p.patient_id);
        const filtered = (Array.isArray(patients) ? patients : [])
          .filter(p => !existingIds.includes(p.patient_id));
        setSearchResults(filtered);
      } catch (err) {
        console.error('Error searching patients:', err);
      }
    };

    const debounce = setTimeout(searchPatients, 300);
    return () => clearTimeout(debounce);
  }, [searchQuery, participants]);

  // Lägg till deltagare (egen bokning; i serie: bara detta eller alla kommande)
  const handleAddParticipant = async (patient) => {
    const scope = await chooseScope(`Lägg till ${patient.fullname || 'deltagaren'}`);
    if (!scope) return false;
    try {
      setAdding(true);
      setError(null);
      await addGroupParticipant(await ensureRealId(), patient.patient_id, null, scope,
        confirmationPayload(confirmation));
      // Hämta uppdaterad lista
      await loadParticipants();
      setSearchQuery('');
      setSearchResults([]);
      if (onUpdate) onUpdate();
      return true;
    } catch (err) {
      console.error('Error adding participant:', err);
      setError(err.response?.data?.error || 'Kunde inte lägga till deltagare');
      return false;
    } finally {
      setAdding(false);
    }
  };

  // Ny patient (gemensamt formulär) är redan skapad — lägg till som deltagare (G2),
  // utan att lämna modalen
  const askNewPatientScope = async () => {
    const scope = await chooseScope('Lägg till den nya patienten');
    newPatientScopeRef.current = scope || 'this';
    return !!scope;
  };

  const handleNewPatientCreated = async (patient) => {
    const scope = newPatientScopeRef.current;
    try {
      setAdding(true);
      setError(null);
      await addGroupParticipant(await ensureRealId(), patient.patient_id, null, scope,
        confirmationPayload(confirmation));
      // Hämta uppdaterad lista
      await loadParticipants();
      setShowNewPatientForm(false);
      if (onUpdate) onUpdate();
    } catch (err) {
      console.error('Error adding new patient as participant:', err);
      setError(err.response?.data?.error || 'Patienten skapades men kunde inte läggas till som deltagare');
    } finally {
      setAdding(false);
    }
  };

  // Personnumret finns redan på kliniken → lägg till den befintliga patienten
  const handleExistingPatient = async (patient) => {
    if (await handleAddParticipant(patient)) setShowNewPatientForm(false);
  };

  // Ta bort deltagare = avboka deltagarens bokning (raden finns kvar som Avbokad)
  const handleRemoveParticipant = async (participant) => {
    const name = participant.patient?.fullname || 'deltagaren';
    let scope = 'this';
    if (isSeries) {
      scope = await askScope(`Avboka ${name}`,
        'Bokningen avbokas och finns kvar i historiken. Genomförda, uteblivna och betalda tillfällen lämnas orörda.');
      if (!scope) return;
    } else if (!window.confirm(`Avboka ${name} från gruppen? Bokningen finns kvar i historiken som avbokad.`)) {
      return;
    }
    try {
      const res = await removeGroupParticipant(await ensureRealId(), participant.patient_id, scope);
      await loadParticipants();
      if (res?.kept) {
        alert(`${res.kept} tillfälle(n) är redan genomförda, uteblivna eller betalda och avbokades inte.`);
      }
      if (onUpdate) onUpdate();
    } catch (err) {
      console.error('Error removing participant:', err);
      setError(err.response?.data?.error || 'Kunde inte avboka deltagaren');
    }
  };

  // Uppdatera deltagarstatus. "Avbokad" går via avbokningsflödet (omfång,
  // betalda/genomförda skyddas) - inte som ett rått statusbyte.
  const handleUpdateStatus = async (participant, newStatus) => {
    if (newStatus === 'cancelled') {
      await handleRemoveParticipant(participant);
      return;
    }
    try {
      await updateParticipantStatus(await ensureRealId(), participant.patient_id, newStatus);
      await loadParticipants();
      if (onUpdate) onUpdate();
    } catch (err) {
      console.error('Error updating participant status:', err);
      setError(err.response?.data?.error || 'Kunde inte uppdatera status');
    }
  };

  // Öppna betalningsmodal
  const openPaymentModal = (participant) => {
    setPaymentModal(participant);
    // Sätt default belopp från bokningstyp
    if (appointment.appointment_type?.default_price) {
      setPaymentAmount(appointment.appointment_type.default_price.toString());
    } else {
      setPaymentAmount('200');
    }
    setPaymentMethod('card');
    setIsFriskvard(false);
  };

  // Registrera betalning
  const handleRegisterPayment = async () => {
    // Uteblivet/frikort/avgiftsfritt utan belopp är ok - uteblivet får
    // klinikens fasta avgift av backend, övriga registreras som 0 kr
    if (!paymentModal || (!paymentAmount && !UTAN_BELOPP.includes(paymentMethod))) return;

    try {
      setProcessingPayment(true);
      const payload = {
        payment_method: paymentMethod,
        is_friskvard: isFriskvard
      };
      // Frikort registreras alltid som 0 kr (patienten har frikort). Uteblivet
      // skickar aldrig belopp - backend använder klinikens fasta avgift (annars
      // skulle det förifyllda behandlingspriset faktureras).
      if (paymentMethod === 'frikort' || paymentMethod === 'avgiftsfri') {
        payload.amount = 0;
      } else if (paymentAmount && paymentMethod !== 'uteblivet') {
        payload.amount = parseFloat(paymentAmount);
      }
      await registerParticipantPayment(await ensureRealId(), paymentModal.patient_id, payload);

      // Uppdatera listan
      await loadParticipants();

      setPaymentModal(null);
      if (onUpdate) onUpdate();
    } catch (err) {
      console.error('Error registering payment:', err);
      setError(err.response?.data?.error || 'Kunde inte registrera betalning');
    } finally {
      setProcessingPayment(false);
    }
  };

  // Boka från väntelista
  const handleBookFromWaitlist = async () => {
    if (!selectedTemplate || waitlist.length === 0) return;

    // H: bekräfta uttaget - de bokade tas samtidigt bort från väntelistan.
    let scope = 'this';
    if (isSeries) {
      scope = await askScope(`Boka ${waitlist.length} patient(er) från väntelistan`,
        'De tas då bort från väntelistan.');
      if (!scope) return;
    } else if (!window.confirm(
      `Boka ${waitlist.length} patient(er) från väntelistan till gruppen?\n\n` +
      'De tas då bort från väntelistan.'
    )) {
      return;
    }

    try {
      setLoadingWaitlist(true);
      await bookFromWaitlist(selectedTemplate, await ensureRealId(), null, scope,
        confirmationPayload(confirmation));
      // Uppdatera deltagarlistan
      await loadParticipants();
      // Uppdatera väntelistan
      const newWaitlist = await getWaitlist(selectedTemplate);
      setWaitlist(newWaitlist || []);
      setShowWaitlist(false);
      if (onUpdate) onUpdate();
    } catch (err) {
      console.error('Error booking from waitlist:', err);
      setError(err.response?.data?.error || 'Kunde inte boka från väntelista');
    } finally {
      setLoadingWaitlist(false);
    }
  };

  // Förläng serien: hämta slutdatum + sista tillfällets deltagare, fråga om de ska följa med
  // Ett virtuellt tillfälle materialiseras inte bara av att panelen öppnas -
  // seriens id räcker för förlängningen
  const seriesRefId = () =>
    realIdRef.current || appointment.original_appointment_id || appointment.appointment_id;

  const openExtendPanel = async () => {
    try {
      setError(null);
      const info = await getGroupSeriesInfo(seriesRefId());
      setExtendPanel({ ...info, newEnd: info.max_end_date, includeParticipants: true });
    } catch (err) {
      console.error('Error loading series info:', err);
      setError(err.response?.data?.error || 'Kunde inte hämta serien');
    }
  };

  const handleExtendSeries = async () => {
    if (!extendPanel?.newEnd) return;
    try {
      setExtending(true);
      setError(null);
      const res = await extendGroupSeries(seriesRefId(), extendPanel.newEnd,
        extendPanel.includeParticipants);
      setExtendPanel(null);
      alert(`Serien förlängd till ${res.recurrence_end_date}: ${res.occurrences_created} nya tillfällen`
        + (res.occurrences_reopened ? `, ${res.occurrences_reopened} återöppnade` : '')
        + (extendPanel.includeParticipants ? `, ${res.participant_bookings_created} deltagarbokningar.` : '.'));
      if (onUpdate) onUpdate();
    } catch (err) {
      console.error('Error extending series:', err);
      setError(err.response?.data?.error || 'Kunde inte förlänga serien');
    } finally {
      setExtending(false);
    }
  };

  // Öppna/stäng gruppjournal-panelen. Förvälj aktiva deltagare (ej avbokade).
  const toggleJournalPanel = () => {
    if (showJournalPanel) {
      setShowJournalPanel(false);
      return;
    }
    const preselected = new Set(
      participants
        .filter(p => p.status !== 'cancelled')
        .map(p => p.patient_id)
    );
    setJournalRecipients(preselected);
    setJournalSuccess(null);
    setShowJournalPanel(true);
  };

  // Kryssa i/ur en mottagare
  const toggleJournalRecipient = (patientId) => {
    setJournalRecipients(prev => {
      const next = new Set(prev);
      if (next.has(patientId)) {
        next.delete(patientId);
      } else {
        next.add(patientId);
      }
      return next;
    });
  };

  // Pusha journalanteckningen till valda deltagare (G4)
  const handlePushJournal = async () => {
    const recipientIds = Array.from(journalRecipients);
    if (recipientIds.length === 0) {
      setError('Välj minst en deltagare att pusha journalen till');
      return;
    }
    if (!journalText.trim()) {
      setError('Skriv en journaltext först');
      return;
    }

    // Dubbelvarning: någon av mottagarna har redan en anteckning idag
    const dupes = participants.filter(
      p => journalRecipients.has(p.patient_id) && p.has_journal_entry
    );
    if (dupes.length > 0) {
      const names = dupes.map(p => p.patient?.fullname || p.fullname).filter(Boolean).join(', ');
      const proceed = window.confirm(
        `${dupes.length} av de valda deltagarna har redan en journalanteckning idag` +
        (names ? ` (${names})` : '') +
        '.\n\nVill du skapa ytterligare en anteckning för dem ändå?'
      );
      if (!proceed) return;
    }

    try {
      setPushingJournal(true);
      setError(null);
      // Bygg sektioner från vald journalmall (som NewJournalModal.handleCreate).
      // Den gemensamma texten hamnar i första sektionen; övriga skapas tomma och
      // kan justeras per deltagare i editorn. Saknar kliniken journalmallar
      // faller vi tillbaka på en enda fri sektion (mall-lös).
      const text = journalText.trim();
      const selectedTemplate = journalTemplates.find(
        t => t.template_ref === selectedJournalTemplateRef
      );
      const templateRef = selectedTemplate ? selectedJournalTemplateRef : null;
      const content = selectedTemplate?.sections?.length
        ? {
            sections: selectedTemplate.sections.map((s, idx) => ({
              title: s.section_title,
              content: idx === 0 ? text : ''
            }))
          }
        : { sections: [{ title: 'Anteckning', content: text }] };
      const res = await groupPushJournal(await ensureRealId(), recipientIds, content, templateRef);
      // Uppdatera deltagarlistan så dubbelvarningen speglar de nya utkasten
      await loadParticipants();
      setJournalSuccess(`Journalutkast skapat för ${res.created_count} deltagare.`);
      setJournalText('');
      if (onUpdate) onUpdate();
    } catch (err) {
      console.error('Error pushing group journal:', err);
      setError(err.response?.data?.error || 'Kunde inte pusha journalen');
    } finally {
      setPushingJournal(false);
    }
  };

  // Klick på patient för att öppna journal
  const handlePatientClick = (participant) => {
    if (onOpenPatient) {
      onOpenPatient(participant.patient);
    }
  };

  // Status-label med färg
  const getStatusLabel = (status) => {
    const statusMap = {
      confirmed: { label: 'Bekräftad', class: 'status-confirmed' },
      attended: { label: 'Närvarande', class: 'status-attended' },
      no_show: { label: 'Uteblev', class: 'status-no-show' },
      cancelled: { label: 'Avbokad', class: 'status-cancelled' }
    };
    return statusMap[status] || { label: status, class: '' };
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content group-modal group-modal-large" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-title">
            <h3>Deltagare - {appointment.group_name}</h3>
            <div className="participant-stats">
              <span className="stat-badge stat-total">
                <i className="fas fa-users"></i> {participants.filter(p => p.status !== 'cancelled').length} deltagare
              </span>
              <span className="stat-badge stat-paid">
                <i className="fas fa-check-circle"></i> {paymentStats.paid} betalda
              </span>
              <span className="stat-badge stat-unpaid">
                <i className="fas fa-clock"></i> {paymentStats.unpaid} obetalda
              </span>
            </div>
          </div>
          <button className="close-btn" onClick={onClose}>&times;</button>
        </div>

        <div className="modal-body">
          {error && <div className="calendar-error">{error}</div>}

          {passCancelled && (
            <div className="warning-message" style={{ marginBottom: '12px' }}>
              <i className="fas fa-ban"></i> Passet är avbokat och deltagarna med det. Återaktiveras
              passet förblir deltagarna avbokade — lägg till dem igen här (de får då en ny
              bokningsbekräftelse).
            </div>
          )}

          {/* Förläng serien (Spår G): nytt slutdatum, nuvarande deltagare följer med? */}
          {extendPanel && (
            <div className="group-journal-panel">
              <div className="group-journal-header">
                <h4><i className="fas fa-redo"></i> Förläng serien</h4>
                <span className="group-journal-hint">
                  Nuvarande slutdatum: {extendPanel.recurrence_end_date || '–'}. Nya tillfällen
                  skapas fram till det nya slutdatumet (högst 12 månader fram). Tillfällen som
                  avbokades när serien avslutades öppnas igen — enskilt inställda pass förblir
                  inställda, och den som själv avbokat sig förblir avbokad.
                </span>
              </div>
              <div className="form-group">
                <label>Nytt slutdatum</label>
                <input
                  type="date"
                  value={extendPanel.newEnd || ''}
                  min={extendPanel.recurrence_end_date ? addDaysIso(extendPanel.recurrence_end_date, 1) : undefined}
                  max={extendPanel.max_end_date}
                  onChange={e => setExtendPanel(prev => ({ ...prev, newEnd: e.target.value }))}
                />
              </div>
              <label className="group-journal-recipient">
                <input
                  type="checkbox"
                  checked={extendPanel.includeParticipants}
                  onChange={e => setExtendPanel(prev => ({ ...prev, includeParticipants: e.target.checked }))}
                />
                <span className="recipient-name">
                  Ska nuvarande deltagare följa med?
                  {extendPanel.participants?.length > 0
                    ? ` (${extendPanel.participants.map(p => p.patient?.fullname).filter(Boolean).join(', ')})`
                    : ' (inga aktiva deltagare på sista tillfället)'}
                </span>
              </label>
              <div className="group-journal-actions">
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleExtendSeries}
                  disabled={extending || !extendPanel.newEnd}
                >
                  {extending ? 'Förlänger...' : 'Förläng serien'}
                </button>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setExtendPanel(null)}
                  disabled={extending}
                >
                  Avbryt
                </button>
              </div>
            </div>
          )}

          {/* Bekräftelse när deltagare läggs till (Spår B3) */}
          {!passCancelled && (
            <BookingConfirmationChoice
              compact
              options={confirmationOptions}
              value={confirmation}
              onChange={setConfirmation}
              title="Skicka bokningsbekräftelse när deltagare läggs till"
            />
          )}

          {/* Sök och lägg till deltagare */}
          {!passCancelled && <div className="add-participant-section">
            <div className="form-group">
              <label>Lägg till deltagare</label>
              <div className="participant-search">
                <input
                  type="text"
                  className="patient-search-input"
                  placeholder="Sök patient att lägga till..."
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  disabled={adding}
                />
                {searchResults.length > 0 && (
                  <div className="patient-search-results">
                    {searchResults.map(patient => (
                      <div
                        key={patient.patient_id}
                        className="patient-search-item"
                        onClick={() => handleAddParticipant(patient)}
                      >
                        <div className="patient-name">{patient.fullname}</div>
                        <div className="patient-pnr">{patient.personnummer}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Lägg till HELT ny patient som deltagare (G2) */}
              {!showNewPatientForm ? (
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  style={{ marginTop: '8px' }}
                  onClick={() => setShowNewPatientForm(true)}
                  disabled={adding}
                >
                  + Ny patient
                </button>
              ) : (
                <div className="new-patient-inline">
                  <h4>Ny patient som deltagare</h4>
                  <NewPatientForm
                    compact
                    showAddress={false}
                    submitLabel="Skapa och lägg till"
                    beforeCreate={askNewPatientScope}
                    onCreated={handleNewPatientCreated}
                    onExisting={handleExistingPatient}
                    existingLabel="Lägg till som deltagare"
                    onCancel={() => setShowNewPatientForm(false)}
                  />
                </div>
              )}
            </div>

            {/* Väntelista-knapp */}
            {templates.length > 0 && (
              <button
                type="button"
                className="btn btn-outline-primary btn-waitlist"
                onClick={() => setShowWaitlist(!showWaitlist)}
              >
                <i className="fas fa-list"></i>
                Lägg till från väntelista
              </button>
            )}

            {/* Gruppjournal-knapp (G4) */}
            {participants.length > 0 && (
              <button
                type="button"
                className="btn btn-outline-primary btn-waitlist"
                onClick={toggleJournalPanel}
              >
                <i className="fas fa-notes-medical"></i>
                Skriv journal till deltagare
              </button>
            )}
          </div>}

          {/* Gruppjournal-panel (G4): skriv en gång → pusha till valda */}
          {showJournalPanel && (
            <div className="group-journal-panel">
              <div className="group-journal-header">
                <h4><i className="fas fa-notes-medical"></i> Journal till deltagare</h4>
                <span className="group-journal-hint">
                  Skriv en anteckning och välj vilka deltagare den skapas för. Varje
                  deltagare får ett eget utkast som kan justeras individuellt.
                </span>
              </div>

              {journalSuccess && (
                <div className="group-journal-success">
                  <i className="fas fa-check-circle"></i> {journalSuccess}
                </div>
              )}

              {journalTemplates.length > 0 ? (
                <div className="group-journal-template">
                  <label className="group-journal-template-label">Journalmall</label>
                  <select
                    className="template-select"
                    value={selectedJournalTemplateRef}
                    onChange={e => setSelectedJournalTemplateRef(e.target.value)}
                    disabled={pushingJournal}
                  >
                    {journalTemplates.map(t => (
                      <option key={t.template_ref} value={t.template_ref}>
                        {t.template_name}
                      </option>
                    ))}
                  </select>
                </div>
              ) : (
                <div className="group-journal-hint">
                  Inga journalmallar tillgängliga – anteckningen skapas utan mall.
                </div>
              )}

              <textarea
                className="group-journal-text"
                value={journalText}
                onChange={e => setJournalText(e.target.value)}
                placeholder="Skriv journalanteckningen som ska pushas till valda deltagare..."
                rows={5}
              />

              <div className="group-journal-recipients">
                <div className="group-journal-recipients-header">
                  Mottagare ({journalRecipients.size} valda)
                </div>
                {participants.map(p => {
                  const name = p.patient?.fullname || p.fullname || 'Okänd patient';
                  const checked = journalRecipients.has(p.patient_id);
                  return (
                    <label key={p.patient_id} className="group-journal-recipient">
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleJournalRecipient(p.patient_id)}
                      />
                      <span className="recipient-name">{name}</span>
                      {p.has_journal_entry && (
                        <span className="recipient-warning" title="Har redan en anteckning idag">
                          <i className="fas fa-exclamation-triangle"></i> har anteckning idag
                        </span>
                      )}
                    </label>
                  );
                })}
              </div>

              <div className="group-journal-actions">
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handlePushJournal}
                  disabled={pushingJournal || journalRecipients.size === 0 || !journalText.trim()}
                >
                  {pushingJournal ? 'Pushar...' : `Pusha till ${journalRecipients.size} deltagare`}
                </button>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowJournalPanel(false)}
                  disabled={pushingJournal}
                >
                  Stäng
                </button>
              </div>
            </div>
          )}

          {/* Väntelista-sektion */}
          {showWaitlist && (
            <div className="waitlist-section">
              <div className="waitlist-header">
                <select
                  value={selectedTemplate || ''}
                  onChange={e => setSelectedTemplate(e.target.value ? parseInt(e.target.value) : null)}
                  className="template-select"
                >
                  <option value="">Välj gruppmall...</option>
                  {templates.map(t => (
                    <option key={t.template_id} value={t.template_id}>
                      {t.name} ({t.waitlist_count} i kö)
                    </option>
                  ))}
                </select>
                {waitlist.length > 0 && (
                  <button
                    type="button"
                    className="btn btn-primary btn-sm"
                    onClick={handleBookFromWaitlist}
                    disabled={loadingWaitlist}
                  >
                    <i className="fas fa-plus-circle"></i>
                    Boka alla ({waitlist.length})
                  </button>
                )}
              </div>
              {loadingWaitlist ? (
                <div className="loading-text">Laddar väntelista...</div>
              ) : waitlist.length > 0 ? (
                <div className="waitlist-items">
                  {waitlist.map(entry => (
                    <div key={entry.waitlist_id} className="waitlist-item">
                      <span className="waitlist-name">{entry.patient?.fullname}</span>
                      <span className="waitlist-priority">#{entry.priority}</span>
                    </div>
                  ))}
                </div>
              ) : selectedTemplate ? (
                <div className="empty-text">Ingen i väntelistan</div>
              ) : null}
            </div>
          )}

          {/* Lista över deltagare */}
          {loading ? (
            <div className="loading-text">Laddar deltagare...</div>
          ) : participants.length === 0 ? (
            <div className="empty-text">Inga deltagare än</div>
          ) : (
            <div className="participants-table">
              <div className="participants-header">
                <div className="col-name">Namn</div>
                <div className="col-status">Status</div>
                <div className="col-payment">Betalning</div>
                <div className="col-actions">Åtgärder</div>
              </div>
              {participants.map(participant => {
                const statusInfo = getStatusLabel(participant.status);
                const isPaid = participant.payment?.is_paid;
                const isNoShowFee = participant.payment?.is_no_show_fee;
                return (
                  <div key={participant.patient_id} className={`participant-row ${isPaid ? 'paid' : 'unpaid'}`}>
                    <div className="col-name">
                      <span
                        className="patient-name-link"
                        onClick={() => handlePatientClick(participant)}
                        title="Öppna patientjournal"
                      >
                        {participant.patient?.fullname || participant.fullname}
                        <i className="fas fa-external-link-alt link-icon"></i>
                      </span>
                      {participant.has_journal_entry && (
                        <span className="has-journal-badge" title="Har journalanteckning idag">
                          <i className="fas fa-notes-medical"></i>
                        </span>
                      )}
                    </div>
                    <div className="col-status">
                      {participant.status === 'cancelled' ? (
                        <span className={`status-select ${statusInfo.class}`}>
                          Avbokad{' '}
                          {!passCancelled && (
                            <button
                              type="button"
                              className="btn btn-sm btn-link"
                              onClick={() => handleAddParticipant(participant.patient || participant)}
                              title="Lägg till deltagaren igen (ny bokningsbekräftelse)"
                            >
                              Lägg till igen
                            </button>
                          )}
                        </span>
                      ) : (
                        <select
                          value={participant.status}
                          onChange={e => handleUpdateStatus(participant, e.target.value)}
                          className={`status-select ${statusInfo.class}`}
                          disabled={passCancelled}
                        >
                          <option value="confirmed">Bekräftad</option>
                          <option value="attended">Närvarande</option>
                          <option value="no_show">Uteblev</option>
                          <option value="cancelled">Avbokad…</option>
                        </select>
                      )}
                    </div>
                    <div className="col-payment">
                      {isPaid ? (
                        <div className="payment-info paid">
                          <i className="fas fa-check-circle"></i>
                          <span>{participant.payment.amount} kr</span>
                          <span className="payment-method">
                            ({paymentMethodLabel(participant.payment.payment_method)})
                          </span>
                        </div>
                      ) : isNoShowFee ? (
                        <div className="payment-info" title="Uteblivet-avgiften faktureras - ingen betalning">
                          <i className="fas fa-user-slash"></i>
                          <span>Uteblivet ({participant.payment.amount} kr)</span>
                        </div>
                      ) : participant.status === 'cancelled' ? (
                        <span className="info-text">–</span>
                      ) : (
                        <button
                          type="button"
                          className="btn btn-sm btn-pay"
                          onClick={() => openPaymentModal(participant)}
                        >
                          <i className="fas fa-cash-register"></i>
                          Ta betalt
                        </button>
                      )}
                    </div>
                    <div className="col-actions">
                      {participant.status !== 'cancelled' && (
                        <button
                          type="button"
                          className="btn-icon btn-danger-icon"
                          onClick={() => handleRemoveParticipant(participant)}
                          title="Avboka deltagaren (finns kvar i historiken)"
                        >
                          <i className="fas fa-user-minus"></i>
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="modal-footer">
          <div className="left-buttons">
            <span className="info-text">
              <i className="fas fa-calendar"></i>
              {new Date(appointment.start_time).toLocaleString('sv-SE', {
                weekday: 'short',
                day: 'numeric',
                month: 'short',
                hour: '2-digit',
                minute: '2-digit'
              })}
            </span>
          </div>
          <div className="right-buttons">
            {isSeries && !extendPanel && (
              <button type="button" className="btn btn-outline-primary" onClick={openExtendPanel}>
                <i className="fas fa-redo"></i> Förläng serien
              </button>
            )}
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Stäng
            </button>
          </div>
        </div>

        {/* Betalningsmodal */}
        {paymentModal && (
          <div className="payment-modal-overlay" onClick={() => setPaymentModal(null)}>
            <div className="payment-modal" onClick={e => e.stopPropagation()}>
              <div className="payment-modal-header">
                <h4>Registrera betalning</h4>
                <button className="close-btn" onClick={() => setPaymentModal(null)}>&times;</button>
              </div>
              <div className="payment-modal-body">
                <div className="payment-patient-info">
                  <i className="fas fa-user"></i>
                  <span>{paymentModal.patient?.fullname}</span>
                </div>

                {!UTAN_BELOPP.includes(paymentMethod) ? (
                  <div className="form-group">
                    <label>Belopp (kr)</label>
                    <input
                      type="text"
                      inputMode="numeric"
                      value={paymentAmount}
                      onChange={e => setPaymentAmount(e.target.value)}
                      placeholder="0.00"
                    />
                  </div>
                ) : paymentMethod === 'frikort' ? (
                  <div className="form-group">
                    <span className="info-text">
                      <i className="fas fa-info-circle"></i>{' '}
                      Frikort registreras utan belopp (0 kr).
                    </span>
                  </div>
                ) : paymentMethod === 'avgiftsfri' ? (
                  <div className="form-group">
                    <span className="info-text">
                      <i className="fas fa-info-circle"></i>{' '}
                      Markeras som hanterat utan avgift (0 kr), inget kvitto.
                    </span>
                  </div>
                ) : (
                  <div className="form-group">
                    <span className="info-text">
                      <i className="fas fa-info-circle"></i>{' '}
                      Klinikens fasta uteblivet-avgift registreras och fakturaunderlag
                      mailas till fakturamottagaren (0 kr och inget underlag om patienten
                      omfattas av klinikens åldersundantag).
                    </span>
                  </div>
                )}

                <div className="form-group">
                  <label>Betalningsmetod</label>
                  <div className="payment-method-buttons">
                    {PAYMENT_METHODS.map(method => (
                      <button
                        key={method.value}
                        type="button"
                        className={`payment-method-btn ${paymentMethod === method.value ? 'active' : ''}`}
                        onClick={() => setPaymentMethod(method.value)}
                      >
                        <i className={`fas ${method.icon}`}></i>
                        <span>{method.label}</span>
                      </button>
                    ))}
                  </div>
                </div>

                <div className="form-group form-check">
                  <label>
                    <input
                      type="checkbox"
                      checked={isFriskvard}
                      onChange={e => setIsFriskvard(e.target.checked)}
                    />
                    <span>Friskvård</span>
                  </label>
                </div>
              </div>
              <div className="payment-modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setPaymentModal(null)}
                >
                  Avbryt
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleRegisterPayment}
                  disabled={processingPayment || (!paymentAmount && !UTAN_BELOPP.includes(paymentMethod))}
                >
                  {processingPayment ? 'Registrerar...' : 'Registrera betalning'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Val: bara detta tillfälle / detta och alla kommande */}
        {scopeDialog}
      </div>
    </div>
  );
};

export default GroupParticipantsModal;
